import React, { useEffect, useRef, useState } from 'react';
import '../css/InstallPWA.css';

const getPlatform = () => {
  const { userAgent, maxTouchPoints } = window.navigator;
  if (/iPad|iPhone|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)) return 'ios';
  return /Android/i.test(userAgent) ? 'android' : 'desktop';
};

export default function InstallPWA() {
  const [platform] = useState(getPlatform);
  const [prompt, setPrompt] = useState(null);
  const promptRef = useRef(null);
  const busyRef = useRef(false);
  const [installed, setInstalled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [status, setStatus] = useState('');
  const embedded = /FBAN|FBAV|Instagram|; wv\)/i.test(window.navigator.userAgent);

  useEffect(() => {
    const displayMode = window.matchMedia('(display-mode: standalone)');
    const checkInstalled = () => {
      if (displayMode.matches || window.navigator.standalone === true) setInstalled(true);
    };
    checkInstalled();
    const onPrompt = (event) => {
      event.preventDefault();
      promptRef.current = event;
      setPrompt(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      promptRef.current = null;
      setPrompt(null);
      setBusy(false);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    displayMode.addEventListener?.('change', checkInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      displayMode.removeEventListener?.('change', checkInstalled);
    };
  }, []);

  const install = async () => {
    if (busyRef.current) return;
    const event = promptRef.current;
    if (!event) {
      setGuideOpen(true);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setStatus('');
    promptRef.current = null;
    setPrompt(null);
    try {
      await event.prompt();
      const choice = await event.userChoice;
      setStatus(choice.outcome === 'accepted'
        ? 'Solicitud aceptada. Completa la instalación en tu dispositivo.'
        : 'Puedes instalarla más adelante desde el menú de tu navegador.');
    } catch {
      setStatus('No se pudo abrir la instalación. Sigue los pasos que aparecen a continuación.');
      setGuideOpen(true);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  if (installed) return null;

  return (
    <section className="capsos-install" id="instalar-app" aria-labelledby="capsos-install-title">
      <div className="capsos-install__card">
        <div className="capsos-install__visual" aria-hidden="true">
          <div className="capsos-install__phone">
            <div className="capsos-install__speaker" />
            <div className="capsos-install__screen">
              <img src={`${process.env.PUBLIC_URL}/logoMovil.png`} alt="" width="46" height="46" />
              <strong>CAPSOS</strong>
              <span>Siempre contigo</span>
              <div className="capsos-install__phone-feature">
                <i className="fas fa-wifi" />
                <span>Tu conexión<strong>Internet para ti</strong></span>
              </div>
              <div className="capsos-install__phone-tiles">
                <div><i className="fas fa-play" /><span>Señal en vivo</span></div>
                <div><i className="fas fa-headset" /><span>Contáctanos</span></div>
              </div>
              <div className="capsos-install__home-bar" />
            </div>
          </div>
          <div className="capsos-install__phone capsos-install__phone--live">
            <div className="capsos-install__speaker" />
            <div className="capsos-install__screen capsos-install__screen--live">
              <div className="capsos-install__live-brand">
                <img src={`${process.env.PUBLIC_URL}/logoMovil.png`} alt="" width="24" height="24" />
                <strong>CAPSOS</strong><i className="fas fa-bars" />
              </div>
              <span className="capsos-install__live-label"><span /> EN VIVO</span>
              <strong className="capsos-install__live-title">Nuestra señal,<br />donde estés.</strong>
              <div className="capsos-install__live-player">
                <img src={`${process.env.PUBLIC_URL}/logoMovil.png`} alt="" width="48" height="48" />
                <span className="capsos-install__live-play"><i className="fas fa-play" /></span>
                <div className="capsos-install__live-controls"><i className="fas fa-volume-up" /><span /><i className="fas fa-expand" /></div>
              </div>
              <div className="capsos-install__live-caption"><i className="fas fa-broadcast-tower" /><span>Señal en vivo<strong>CAPSOS Telecomunicaciones</strong></span></div>
              <span className="capsos-install__live-note">Conectados con nuestra gente</span>
              <div className="capsos-install__home-bar" />
            </div>
          </div>
          <span className="capsos-install__visual-badge"><i className="fas fa-check" /> A un toque de ti</span>
        </div>
        <div className="capsos-install__intro">
          <img src={`${process.env.PUBLIC_URL}/app-icon-movil-192.png`} width="72" height="72" alt="" />
          <div>
            <span className="capsos-install__eyebrow">CAPSOS EN TU CELULAR</span>
            <h2 id="capsos-install-title">Lleva CAPSOS<br /><span>siempre contigo.</span></h2>
            <p>Agrega CAPSOS a tu pantalla de inicio para consultar planes, ver nuestra señal y comunicarte con nosotros.</p>
            <ul className="capsos-install__benefits">
              <li><i className="fas fa-wifi" aria-hidden="true" /> Tus planes</li>
              <li><i className="fas fa-play" aria-hidden="true" /> Señal en vivo</li>
              <li><i className="fas fa-headset" aria-hidden="true" /> Atención directa</li>
            </ul>
          </div>
        </div>
        <div className="capsos-install__actions">
          <button type="button" className="capsos-install__primary" onClick={install} disabled={busy}>
            <i className="fas fa-arrow-down" aria-hidden="true" /> {busy ? 'Abriendo instalación…' : prompt ? 'Instalar CAPSOS' : 'Cómo instalar CAPSOS'}
          </button>
          <span>Gratis · Sin buscar en una tienda</span>
        </div>
        <div className="capsos-install__status" role="status">{status}</div>
        <details className="capsos-install__guide" open={guideOpen} onToggle={(event) => setGuideOpen(event.currentTarget.open)}>
          <summary>Guía de instalación en {platform === 'ios' ? 'iPhone o iPad' : platform === 'android' ? 'Android' : 'tu dispositivo'}</summary>
          {embedded && <p className="capsos-install__notice">Estás usando el navegador de otra aplicación. Abre esta página en Safari (iPhone) o Chrome (Android) desde su menú para instalarla.</p>}
          {platform === 'ios' ? (
            <ol>
              <li>Abre esta página en <strong>Safari</strong>.</li>
              <li>Abre el menú y toca <strong>Compartir</strong> (el cuadrado con una flecha hacia arriba).</li>
              <li>Selecciona <strong>Agregar a pantalla de inicio</strong>. Si aparece <strong>Abrir como app web</strong>, déjalo activado.</li>
              <li>Toca <strong>Agregar</strong>. Encontrarás el icono de CAPSOS en tu pantalla de inicio.</li>
            </ol>
          ) : platform === 'android' ? (
            <ol>
              <li>Abre esta página en <strong>Chrome</strong>.</li>
              <li>Toca el menú de <strong>tres puntos</strong> y busca <strong>Instalar aplicación</strong> o <strong>Agregar a pantalla de inicio</strong>.</li>
              <li>Confirma la opción disponible y abre CAPSOS desde su nuevo icono.</li>
            </ol>
          ) : (
            <ol>
              <li>Abre esta página en <strong>Chrome o Edge</strong>.</li>
              <li>Busca el icono de instalación en la barra de direcciones o la opción de instalar en el menú del navegador.</li>
              <li>Confirma la instalación. Para agregarla a tu celular, abre esta misma página desde ese dispositivo.</li>
            </ol>
          )}
          <p>Las opciones dependen de tu navegador. Si no aparecen, puedes seguir usando la página normalmente. La señal en vivo, WhatsApp y los trámites requieren conexión a internet.</p>
        </details>
      </div>
    </section>
  );
}
